import React, { useEffect, useMemo, useState } from "react";
import { MessageCircle, Plus, Send, ArrowLeft, Trash2, Pencil, X, Check } from "lucide-react";
import api from "../api";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";

const STAGE_THEME = {
  embarazo: { title: "Embarazo", text: "text-rose", hex: "#D4A8B0", soft: "rgba(212,168,176,0.15)" },
  puerperio: { title: "Puerperio", text: "text-terracotta", hex: "#C4705A", soft: "rgba(196,112,90,0.14)" },
  menopausia: { title: "Menopausia", text: "text-plum", hex: "#7A5C9E", soft: "rgba(122,92,158,0.14)" },
};
const ALL_FORUMS = ["embarazo", "puerperio", "menopausia"];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function Comunidad() {
  const { user, isAdmin } = useAuth();
  const forums = isAdmin || !user?.stage ? ALL_FORUMS : ALL_FORUMS.filter((s) => s === user.stage);

  const [threads, setThreads] = useState([]);
  const [activeStage, setActiveStage] = useState(forums[0] || "embarazo");
  const [newTopic, setNewTopic] = useState("");
  const [newBody, setNewBody] = useState("");
  const [selected, setSelected] = useState(null); // thread detail
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [editing, setEditing] = useState(null); // {title, body}
  const [editingReply, setEditingReply] = useState(null); // {id, body}
  const { toast } = useToast();

  const mine = (item) => isAdmin || (item && user && item.author_id === user.id);

  const load = async () => {
    try {
      const { data } = await api.get("/threads");
      setThreads(data);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const stageThreads = useMemo(() => threads.filter((t) => t.stage === activeStage), [threads, activeStage]);
  const counts = useMemo(() => {
    const c = {};
    ALL_FORUMS.forEach((s) => (c[s] = threads.filter((t) => t.stage === s).length));
    return c;
  }, [threads]);

  const theme = STAGE_THEME[activeStage];

  const addTopic = async (e) => {
    e.preventDefault();
    if (!newTopic.trim()) return;
    try {
      const { data } = await api.post("/threads", { stage: activeStage, title: newTopic, body: newBody });
      setThreads((prev) => [data, ...prev]);
      setNewTopic("");
      setNewBody("");
      toast({ title: "Tema publicado" });
    } catch (err) {
      toast({ title: "No se pudo publicar", description: err.response?.data?.detail || "" });
    }
  };

  const openThread = async (t) => {
    setSelected(t);
    setEditing(null);
    setReplyText("");
    try {
      const { data } = await api.get(`/threads/${t.id}`);
      setSelected(data.thread);
      setReplies(data.replies);
    } catch {}
  };

  const closeThread = () => {
    setSelected(null);
    setReplies([]);
    setEditing(null);
    load();
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    try {
      const { data } = await api.post(`/threads/${selected.id}/replies`, { body: replyText });
      setReplies((prev) => [...prev, data]);
      setReplyText("");
      setThreads((prev) => prev.map((t) => (t.id === selected.id ? { ...t, replies: (t.replies || 0) + 1 } : t)));
    } catch (err) {
      toast({ title: "No se pudo responder", description: err.response?.data?.detail || "" });
    }
  };

  const deleteThread = async (t) => {
    try {
      await api.delete(`/threads/${t.id}`);
      setThreads((prev) => prev.filter((x) => x.id !== t.id));
      if (selected?.id === t.id) closeThread();
      toast({ title: "Tema eliminado" });
    } catch (err) {
      toast({ title: "No se pudo eliminar", description: err.response?.data?.detail || "" });
    }
  };

  const saveEdit = async () => {
    try {
      const { data } = await api.put(`/threads/${selected.id}`, { title: editing.title, body: editing.body });
      setSelected(data);
      setThreads((prev) => prev.map((t) => (t.id === data.id ? { ...t, title: data.title } : t)));
      setEditing(null);
      toast({ title: "Tema actualizado" });
    } catch (err) {
      toast({ title: "No se pudo actualizar", description: err.response?.data?.detail || "" });
    }
  };

  const deleteReply = async (r) => {
    try {
      await api.delete(`/threads/${selected.id}/replies/${r.id}`);
      setReplies((prev) => prev.filter((x) => x.id !== r.id));
      setThreads((prev) => prev.map((t) => (t.id === selected.id ? { ...t, replies: Math.max(0, (t.replies || 1) - 1) } : t)));
    } catch {}
  };

  const saveReplyEdit = async () => {
    try {
      const { data } = await api.put(`/threads/${selected.id}/replies/${editingReply.id}`, { body: editingReply.body });
      setReplies((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      setEditingReply(null);
      toast({ title: "Respuesta actualizada" });
    } catch (err) {
      toast({ title: "No se pudo actualizar", description: err.response?.data?.detail || "" });
    }
  };

  return (
    <div className="animate-fade-in">
      <section className="bg-gradient-to-b from-amber/30 to-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta">Espacio privado</span>
          <h1 className="font-serif text-5xl md:text-6xl text-plum mt-3">Foros de la comunidad</h1>
          <p className="font-body text-lg text-ink/65 mt-4 max-w-2xl mx-auto">
            Comparte tus vivencias, resuelve dudas y siente el apoyo de otras mujeres que te entienden.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="space-y-3">
          {forums.map((s) => {
            const th = STAGE_THEME[s];
            const isActive = activeStage === s && !selected;
            return (
              <button
                key={s}
                data-testid={`forum-tab-${s}`}
                onClick={() => { setActiveStage(s); setSelected(null); }}
                className="w-full text-left rounded-2xl p-5 transition-all border"
                style={
                  isActive
                    ? { backgroundColor: th.hex, borderColor: th.hex, color: "#FAF6F1", boxShadow: "0 10px 25px -10px " + th.hex }
                    : { backgroundColor: "#fff", borderColor: "rgba(232,196,154,0.4)" }
                }
              >
                <div className="flex items-center gap-2.5">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: isActive ? "#FAF6F1" : th.hex }} />
                  <span className="font-serif text-2xl" style={{ color: isActive ? "#FAF6F1" : th.hex }}>{th.title}</span>
                </div>
                <div className="font-body text-sm mt-1" style={{ color: isActive ? "rgba(250,246,241,0.75)" : "rgba(58,45,40,0.5)" }}>
                  {counts[s] || 0} temas
                </div>
              </button>
            );
          })}
        </aside>

        {/* Main */}
        <div>
          {!selected ? (
            <>
              {/* New topic */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border mb-6" style={{ borderColor: theme.soft, borderTopWidth: 3, borderTopColor: theme.hex }}>
                <form onSubmit={addTopic} className="space-y-3">
                  <div className="relative">
                    <Plus size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
                    <input
                      data-testid="new-topic-input"
                      value={newTopic}
                      onChange={(e) => setNewTopic(e.target.value)}
                      placeholder={`Abrir un nuevo tema en ${theme.title}...`}
                      className="w-full font-body bg-cream border border-amber/40 rounded-xl pl-11 pr-4 py-3 outline-none focus:border-plum transition-colors"
                    />
                  </div>
                  <textarea
                    data-testid="new-topic-body"
                    value={newBody}
                    onChange={(e) => setNewBody(e.target.value)}
                    rows={2}
                    placeholder="Escribe tu mensaje (opcional)..."
                    className="w-full font-body bg-cream border border-amber/40 rounded-xl px-4 py-3 outline-none focus:border-plum transition-colors"
                  />
                  <div className="flex justify-end">
                    <button type="submit" data-testid="publish-topic-btn" className="flex items-center gap-2 text-cream font-sans px-5 py-2.5 rounded-xl transition-opacity hover:opacity-90" style={{ backgroundColor: theme.hex }}>
                      <Send size={16} /> Publicar tema
                    </button>
                  </div>
                </form>
              </div>

              {/* Thread list */}
              <div className="space-y-4">
                {stageThreads.length === 0 ? (
                  <p className="text-center font-body text-ink/50 py-16">
                    Aún no hay temas en este foro. ¡Sé la primera en abrir uno!
                  </p>
                ) : (
                  stageThreads.map((t) => (
                    <div
                      key={t.id}
                      data-testid={`thread-card-${t.id}`}
                      onClick={() => openThread(t)}
                      className="group bg-white rounded-2xl p-5 shadow-sm border hover:shadow-md transition-all flex items-center gap-4 cursor-pointer"
                      style={{ borderColor: "rgba(232,196,154,0.4)", borderLeftWidth: 4, borderLeftColor: theme.hex }}
                    >
                      <div className="w-12 h-12 rounded-full text-cream flex items-center justify-center font-serif text-lg shrink-0" style={{ backgroundColor: theme.hex }}>
                        {(t.author || "M").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif text-xl text-plum group-hover:text-terracotta transition-colors truncate">{t.title}</h3>
                        <p className="font-body text-sm text-ink/50 mt-0.5">por {t.author} · {fmtDate(t.created_at)}</p>
                      </div>
                      <span className="flex items-center gap-1.5 font-body text-sm text-ink/50 shrink-0">
                        <MessageCircle size={16} style={{ color: theme.hex }} /> {t.replies || 0}
                      </span>
                      {mine(t) && (
                        <button
                          data-testid={`delete-thread-${t.id}`}
                          onClick={(e) => { e.stopPropagation(); deleteThread(t); }}
                          className="text-ink/40 hover:text-destructive transition-colors p-1.5 shrink-0"
                          title="Eliminar tema"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            /* ===== Thread detail ===== */
            <div>
              <button onClick={closeThread} data-testid="back-to-forum-btn" className="flex items-center gap-2 font-sans text-sm text-ink/60 hover:text-plum transition-colors mb-4">
                <ArrowLeft size={16} /> Volver al foro
              </button>

              <div className="bg-white rounded-2xl p-6 shadow-sm border" style={{ borderColor: "rgba(232,196,154,0.4)", borderTopWidth: 3, borderTopColor: STAGE_THEME[selected.stage]?.hex }}>
                {editing ? (
                  <div className="space-y-3">
                    <input
                      data-testid="edit-thread-title"
                      value={editing.title}
                      onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                      className="w-full font-serif text-2xl text-plum bg-cream border border-amber/40 rounded-xl px-4 py-2 outline-none focus:border-plum"
                    />
                    <textarea
                      data-testid="edit-thread-body"
                      value={editing.body}
                      onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                      rows={3}
                      className="w-full font-body bg-cream border border-amber/40 rounded-xl px-4 py-2 outline-none focus:border-plum"
                    />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditing(null)} className="flex items-center gap-1.5 font-sans text-sm text-ink/50 px-4 py-2 rounded-lg hover:bg-cream">
                        <X size={15} /> Cancelar
                      </button>
                      <button onClick={saveEdit} data-testid="save-thread-edit" className="flex items-center gap-1.5 font-sans text-sm text-cream px-4 py-2 rounded-lg" style={{ backgroundColor: STAGE_THEME[selected.stage]?.hex }}>
                        <Check size={15} /> Guardar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <span className="font-sans text-[11px] uppercase tracking-[0.15em]" style={{ color: STAGE_THEME[selected.stage]?.hex }}>{STAGE_THEME[selected.stage]?.title}</span>
                      <h2 className="font-serif text-3xl text-plum leading-tight mt-1">{selected.title}</h2>
                      <p className="font-body text-sm text-ink/50 mt-1">por {selected.author} · {fmtDate(selected.created_at)}</p>
                      {selected.body && <p className="font-body text-ink/75 mt-4 leading-relaxed whitespace-pre-wrap">{selected.body}</p>}
                    </div>
                    {mine(selected) && (
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => setEditing({ title: selected.title, body: selected.body || "" })} data-testid="edit-thread-btn" className="text-ink/40 hover:text-plum transition-colors p-2" title="Editar tema">
                          <Pencil size={17} />
                        </button>
                        <button onClick={() => deleteThread(selected)} data-testid="delete-thread-detail-btn" className="text-ink/40 hover:text-destructive transition-colors p-2" title="Eliminar tema">
                          <Trash2 size={17} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Replies */}
              <div className="mt-6 space-y-3">
                <p className="font-sans uppercase tracking-[0.12em] text-xs text-terracotta">
                  {replies.length} {replies.length === 1 ? "respuesta" : "respuestas"}
                </p>
                {replies.map((r) => (
                  <div key={r.id} data-testid={`reply-${r.id}`} className="bg-white rounded-2xl p-4 shadow-sm border border-amber/30 flex gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-serif text-cream shrink-0" style={{ backgroundColor: r.is_admin ? "#7A5C9E" : "#C4705A" }}>
                      {(r.author || "M").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm">
                        <span className="font-semibold text-plum">{r.author}</span>
                        {r.is_admin && <span className="ml-2 text-[10px] uppercase tracking-wide bg-plum/10 text-plum px-2 py-0.5 rounded-full">Lorena</span>}
                        <span className="text-ink/40 ml-2">· {fmtDate(r.created_at)}</span>
                      </p>
                      {editingReply && editingReply.id === r.id ? (
                        <div className="mt-2 space-y-2">
                          <textarea
                            data-testid={`edit-reply-input-${r.id}`}
                            value={editingReply.body}
                            onChange={(e) => setEditingReply({ ...editingReply, body: e.target.value })}
                            rows={3}
                            className="w-full font-body bg-cream border border-amber/40 rounded-xl px-3 py-2 outline-none focus:border-plum text-sm"
                          />
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setEditingReply(null)} className="flex items-center gap-1.5 font-sans text-xs text-ink/50 px-3 py-1.5 rounded-lg hover:bg-cream">
                              <X size={14} /> Cancelar
                            </button>
                            <button onClick={saveReplyEdit} data-testid={`save-reply-edit-${r.id}`} className="flex items-center gap-1.5 font-sans text-xs text-cream px-3 py-1.5 rounded-lg bg-plum">
                              <Check size={14} /> Guardar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="font-body text-ink/75 mt-1 leading-relaxed whitespace-pre-wrap">{r.body}</p>
                      )}
                    </div>
                    {mine(r) && !(editingReply && editingReply.id === r.id) && (
                      <div className="flex gap-0.5 shrink-0">
                        <button onClick={() => setEditingReply({ id: r.id, body: r.body })} data-testid={`edit-reply-${r.id}`} className="text-ink/30 hover:text-plum transition-colors p-1" title="Editar respuesta">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => deleteReply(r)} data-testid={`delete-reply-${r.id}`} className="text-ink/30 hover:text-destructive transition-colors p-1" title="Eliminar respuesta">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {replies.length === 0 && (
                  <p className="font-body text-ink/45 text-sm py-4">Todavía no hay respuestas. Escribe la primera.</p>
                )}
              </div>

              {/* Reply form */}
              <form onSubmit={sendReply} className="mt-5 bg-white rounded-2xl p-4 shadow-sm border border-amber/30">
                <textarea
                  data-testid="reply-input"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={3}
                  placeholder="Escribe tu respuesta..."
                  className="w-full font-body bg-cream border border-amber/40 rounded-xl px-4 py-3 outline-none focus:border-plum transition-colors"
                />
                <div className="flex justify-end mt-3">
                  <button type="submit" data-testid="send-reply-btn" className="flex items-center gap-2 text-cream font-sans px-5 py-2.5 rounded-xl transition-opacity hover:opacity-90" style={{ backgroundColor: STAGE_THEME[selected.stage]?.hex }}>
                    <Send size={16} /> Responder
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
